import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import useSEO from '../hooks/useSEO';

const { FiAward, FiGlobe, FiSettings, FiTrendingUp, FiUsers, FiCheckCircle } = FiIcons;

const About = () => {
  useSEO({
    title: 'About Minipore - Water Treatment Manufacturer',
    description: 'Shanghai Minipore Industrial Co.,Ltd. - ISO9001:2008 certified manufacturer of RO/UF membranes and water treatment equipment. 30+ years experience, serving 50+ countries worldwide.',
    keywords: 'Minipore about, water treatment manufacturer, Shanghai, ISO9001, RO membrane manufacturer, UF membrane manufacturer',
    url: '/about',
  });

  const stats = [
    { label: 'Years of Experience', value: '30+' },
    { label: 'Countries Served', value: '50+' },
    { label: 'Product Lines', value: '10+' },
    { label: 'Satisfied Customers', value: '10000+' },
  ];

  const features = [
    { icon: FiAward, title: 'ISO9001:2008 Certified', description: 'Quality management system certification ensures consistent product quality and reliability across all our manufacturing processes.' },
    { icon: FiGlobe, title: 'Global Vision', description: 'Export-oriented enterprise with perfect procurement and sales network worldwide, serving customers in over 50 countries.' },
    { icon: FiSettings, title: 'Advanced ERP System', description: 'State-of-the-art enterprise resource planning system for stable quality control and reasonable pricing strategies.' },
    { icon: FiTrendingUp, title: 'One-Stop Platform', description: 'Comprehensive water treatment solutions from consumables to complete equipment systems for all your needs.' },
  ];

  const certifications = [
    { name: 'ISO9001:2008', description: 'Quality Management System' },
    { name: 'NSF Certified', description: 'Drinking Water Components' },
    { name: 'CE Marking', description: 'European Conformity' },
    { name: 'RoHS Compliant', description: 'Environmental Standards' },
  ];

  return (
    <div className="pt-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About Minipore</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Shanghai Minipore Industrial Co.,Ltd. is an export-oriented environmental protection enterprise specializing in raw water and sewage treatment consumables, accessories and equipment.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
          {stats.map((stat, index) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="text-center bg-white p-6 rounded-2xl shadow-lg">
              <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment to Excellence</h2>
              <p className="text-lg text-gray-600 mb-6">With an open mind and global vision, relying on a perfect procurement and sales network, we provide high-quality and affordable water treatment products and professional technical services.</p>
              <p className="text-gray-600 mb-8">Our company has established a comprehensive quality management system and uses advanced ERP management system to ensure stable product quality and reasonable pricing. We are committed to serving customers worldwide with our three brands: 华膜, MINIPORE, and MURFILTEC.</p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">华膜 Brand</div>
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">MINIPORE Brand</div>
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">MURFILTEC Brand</div>
              </div>
              <div className="mb-8">
                <h3 className="font-semibold text-gray-900 mb-2">Company Address:</h3>
                <address className="text-gray-600 not-italic">
                  Shanghai Minipore Industrial Co.,Ltd.<br />
                  No 2755, Bao'an Highway, Malu Town, Jiading District,<br />
                  Shanghai, China. 201801
                </address>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <video src="https://filedn.com/lRYNf7eGO3tp3VIi6pVnJX0/totalfilt/c5dedae8d429f703cc8471234f4d18e9.mp4" className="rounded-2xl shadow-lg w-full h-auto" controls autoPlay muted loop />
            </motion.div>
          </div>
        </section>

        <section className="mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Minipore</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="bg-blue-100 p-3 rounded-full w-fit mb-4">
                  <SafeIcon icon={feature.icon} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mb-20 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Product Lines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2 h-5 w-5 text-blue-600" />Industrial Solutions
              </h3>
              <ul className="space-y-2 text-gray-600">
                {['MF microfiltration series - PP, PES, N6, PTFE, PVDF and SS microporous membrane pleated filter cartridges', 'UF ultrafiltration series - PAN, PVC, PS, PVDF hollow fiber UF membrane modules', 'RO reverse osmosis series - ULP, LP, BW and SW desalination RO membrane elements', 'Liquid filter bag series - PP, PE and Nylon monofilament filter bags', 'Industrial filter housings - SS stainless steel and UPVC cartridge and bag style'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiUsers} className="mr-2 h-5 w-5 text-blue-600" />Residential & Accessories
              </h3>
              <ul className="space-y-2 text-gray-600">
                {['Household UF ultrafiltration water purifiers and RO reverse osmosis water purifiers', 'Household PP plastic and SS stainless steel filter housings', 'SS stainless steel and FRP RO pressure vessel housings', 'Multi media FRP and stainless steel filter tanks, softening tanks', 'UV sterilizers, flowmeters, pressure gauges, and control accessories'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Quality Certifications</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div key={cert.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg text-center">
                <div className="bg-blue-100 p-3 rounded-full w-fit mx-auto mb-4">
                  <SafeIcon icon={FiAward} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{cert.name}</h3>
                <p className="text-gray-600 text-sm">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-blue-600 text-white p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold mb-4">Partner with Minipore</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">Join thousands of satisfied customers worldwide who trust Minipore for their water treatment needs.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors">Contact Us</Link>
            <Link to="/products" className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors">View Products</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;